<template>
  <div class="panel">

    <!-- ── PASO 1: Subir carpeta ─────────────────────────────────────── -->
    <section class="step" :class="{ 'step--done': done }">
      <header class="step__header">
        <span class="step__num">1</span>
        <h2 class="step__title">Subir carpeta</h2>
        <span v-if="done" class="step__badge step__badge--ok">✓ Completado</span>
      </header>

      <div
        class="drop-zone"
        :class="{ active: dragging, filled: files.length }"
        @dragover.prevent="dragging = true"
        @dragleave="dragging = false"
        @drop.prevent="onDrop"
      >
        <template v-if="!files.length">
          <p class="title">Arrastra tu carpeta aquí</p>
          <label class="btn">
            Seleccionar carpeta
            <input type="file" webkitdirectory multiple hidden @change="onInput" />
          </label>
        </template>
        <template v-else>
          <p class="title">{{ folderName }}</p>
          <p class="subtitle">{{ files.length }} archivos · {{ totalSize }}</p>
        </template>
      </div>

      <div v-if="uploading || done" class="progress-block">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: globalProgress + '%' }" />
        </div>
        <div class="progress-stats">
          <span>{{ globalProgress }}%</span>
          <span>{{ uploadedCount }}/{{ files.length }} archivos</span>
          <span v-if="speed">{{ speed }}</span>
        </div>
      </div>

      <p v-if="currentFile" class="current-file">↑ {{ currentFile }}</p>

      <div v-if="paused"       class="alert alert--warn">Conexión interrumpida — reanudando automáticamente...</div>
      <div v-if="uploadError"  class="alert alert--error">{{ uploadError }}</div>
      <div v-if="done"         class="alert alert--ok">✓ Carpeta subida correctamente</div>

      <div class="actions">
        <button v-if="files.length && !uploading && !done" class="btn btn--primary" @click="startUpload">Subir carpeta</button>
        <button v-if="uploading"          class="btn btn--ghost"   @click="cancelUpload">Cancelar</button>
        <button v-if="done || uploadError" class="btn btn--outline" @click="reset">Subir otra</button>
      </div>
    </section>

    <!-- ── PASO 2: Indexar PDFs ──────────────────────────────────────── -->
    <section class="step" :class="{ 'step--locked': !done }">
      <header class="step__header">
        <span class="step__num">2</span>
        <h2 class="step__title">Indexar PDFs</h2>
        <span v-if="!done" class="step__badge step__badge--locked">Esperando subida</span>
        <span v-if="estadoFinal === 'COMPLETADO'" class="step__badge step__badge--ok">✓ Completado</span>
        <span v-if="estadoFinal === 'ERROR'"      class="step__badge step__badge--error">✗ Error</span>
      </header>

      <p class="tramite-info">
        Trámite N°: <strong>{{ idTramite ?? '—' }}</strong>
      </p>

      <button
        class="btn btn--primary"
        :disabled="!done || ejecutando"
        @click="ejecutarIndexador"
      >
        {{ ejecutando ? 'Indexando...' : 'Indexar PDFs' }}
      </button>

      <div v-if="lineas.length" class="consola">
        <p v-for="(linea, i) in lineas" :key="i" :class="claseLinea(linea)">{{ linea }}</p>
      </div>

      <div v-if="estadoFinal" :class="estadoFinal === 'COMPLETADO' ? 'alert alert--ok' : 'alert alert--error'">
        {{ estadoFinal === 'COMPLETADO' ? '✅ Indexación completada' : '❌ Error en indexación' }}
      </div>
    </section>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import Swal from 'sweetalert2'
import config from '@/config.js'

// ── Constantes ────────────────────────────────────────────────────────────────
const CHUNK_SIZE   = 5 * 1024 * 1024
const API_BASE     = `${config.apiBaseUrl}/upload`
const CONCURRENCY  = 2
const MAX_RETRIES  = 5
const RETRY_DELAYS = [1000, 3000, 6000, 12000, 24000]

// ── Ruta ──────────────────────────────────────────────────────────────────────
const route     = useRoute()
const idTramite = ref(null)

onMounted(() => {
  idTramite.value = route.params.p_idtramite
})

// ── Estado: Uploader ──────────────────────────────────────────────────────────
const files          = ref([])
const uploading      = ref(false)
const done           = ref(false)
const paused         = ref(false)
const cancelled      = ref(false)
const currentFile    = ref('')
const uploadedCount  = ref(0)
const globalProgress = ref(0)
const speed          = ref('')
const uploadError    = ref('')
const dragging       = ref(false)

let startTime    = 0
let bytesUploaded = 0

// ── Estado: Indexador ─────────────────────────────────────────────────────────
const lineas      = ref([])
const ejecutando  = ref(false)
const estadoFinal = ref(null)
let   eventSource = null

onUnmounted(() => {
  if (eventSource) eventSource.close()
})

// ── Computed ──────────────────────────────────────────────────────────────────
const folderName = computed(() => {
  const first = files.value[0]?.webkitRelativePath || ''
  return first.split('/')[0] || 'Carpeta'
})

const totalSize = computed(() => {
  const mb = files.value.reduce((s, f) => s + f.size, 0) / 1024 / 1024
  return mb > 1024 ? (mb / 1024).toFixed(1) + ' GB' : mb.toFixed(0) + ' MB'
})

// ── Carga de archivos ─────────────────────────────────────────────────────────
function onInput(e) {
  const archivos = [...e.target.files]
  if (!validarNombreCarpeta(archivos)) {
    e.target.value = '' // limpiar el input
    return
  }
  files.value = archivos
}

async function onDrop(e) {
  dragging.value = false
  const all = []
  for (const item of [...e.dataTransfer.items]) {
    const entry = item.webkitGetAsEntry?.()
    if (entry) all.push(...await traverse(entry))
  }
  if (!validarNombreCarpeta(all)) return
  files.value = all
}

async function traverse(entry, path = '') {
  if (entry.isFile) {
    const f = await new Promise(r => entry.file(r))
    f.relativePath = path + f.name
    return [f]
  }
  const entries = await new Promise(r => entry.createReader().readEntries(r))
  const nested  = await Promise.all(entries.map(en => traverse(en, path + entry.name + '/')))
  return nested.flat()
}

// ── Upload principal ──────────────────────────────────────────────────────────
async function startUpload() {
  uploading.value      = true
  done.value           = false
  cancelled.value      = false
  uploadError.value    = ''
  uploadedCount.value  = 0
  globalProgress.value = 0
  startTime            = Date.now()
  bytesUploaded        = 0

  const queue = [...files.value]
  try {
    await Promise.all(
      Array(CONCURRENCY).fill(null).map(() => uploadWorker(queue))
    )
    if (!cancelled.value) {
      done.value        = true
      currentFile.value = ''
    }
  } catch (err) {
    uploadError.value = 'Error al subir: ' + err.message
  } finally {
    uploading.value = false
  }
}

async function uploadWorker(queue) {
  while (queue.length && !cancelled.value) {
    const file = queue.shift()
    await uploadFile(file)
    uploadedCount.value++
    globalProgress.value = Math.round(uploadedCount.value / files.value.length * 100)
  }
}

async function uploadFile(file) {
  const relativePath = file.relativePath || file.webkitRelativePath
  const fileId       = getOrCreateFileId(file)
  const totalChunks  = Math.ceil(file.size / CHUNK_SIZE)
  currentFile.value  = relativePath

  const alreadyReceived = await fetchReceivedChunks(fileId)
  const pending = Array.from({ length: totalChunks }, (_, i) => i)
    .filter(i => !alreadyReceived.includes(i))

  for (const index of pending) {
    if (cancelled.value) return
    const start = index * CHUNK_SIZE
    const chunk = file.slice(start, start + CHUNK_SIZE)
    await uploadChunkWithRetry(chunk, { fileId, index, totalChunks, relativePath })
    bytesUploaded += chunk.size
    const elapsed = (Date.now() - startTime) / 1000
    speed.value   = (bytesUploaded / 1024 / 1024 / elapsed).toFixed(1) + ' MB/s'
  }

  localStorage.removeItem('upload_' + file.name + '_' + file.size)
}

async function uploadChunkWithRetry(chunk, { fileId, index, totalChunks, relativePath }) {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      paused.value = false
      const formData = new FormData()
      formData.append('file',         chunk)
      formData.append('fileId',       fileId)
      formData.append('chunkIndex',   index)
      formData.append('totalChunks',  totalChunks)
      formData.append('relativePath', relativePath)

      const res = await fetch(`${API_BASE}/chunk`, { method: 'POST', body: formData })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return
    } catch (err) {
      if (attempt === MAX_RETRIES) throw err
      paused.value = true
      await sleep(RETRY_DELAYS[attempt] ?? 24000)
    }
  }
}

function validarNombreCarpeta(archivos) {
  const primerArchivo = archivos[0]
  if (!primerArchivo) return false

  const rutaRelativa = primerArchivo.webkitRelativePath || primerArchivo.relativePath || ''
  const nombreCarpeta = rutaRelativa.split('/')[0]

  if (nombreCarpeta !== String(idTramite.value)) {
    Swal.fire({
      icon: 'warning',
      title: 'Carpeta incorrecta',
      html: `La carpeta debe llamarse <strong>${idTramite.value}</strong>.<br>
             La carpeta seleccionada se llama <strong>${nombreCarpeta}</strong>.`,
      confirmButtonText: 'Entendido'
    })
    return false
  }
  return true
}

// ── Indexador ─────────────────────────────────────────────────────────────────
function ejecutarIndexador() {
  lineas.value      = []
  estadoFinal.value = null
  ejecutando.value  = true
  let finalizado    = false

  eventSource = new EventSource(
    `${config.apiBaseUrl}/indexador/ejecutar/${idTramite.value}`
  )

  eventSource.onmessage = (event) => {
    lineas.value.push(event.data)
  }

  eventSource.addEventListener('fin', (event) => {
    finalizado        = true
    estadoFinal.value = event.data
    ejecutando.value  = false
    eventSource.close()

    if (event.data === 'COMPLETADO') {
      Swal.fire({
        icon: 'success',
        title: 'Indexación completada',
        text: `Trámite ${idTramite.value} procesado correctamente`,
        confirmButtonText: 'Aceptar'
      })
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error en indexación',
        text: `Ocurrió un error al procesar el trámite ${idTramite.value}`,
        confirmButtonText: 'Aceptar'
      })
    }
  })

  eventSource.addEventListener('error', () => {
    if (finalizado) return
    estadoFinal.value = 'ERROR'
    ejecutando.value  = false
    eventSource.close()
    Swal.fire({
      icon: 'error',
      title: 'Error de conexión',
      text: 'No se pudo conectar con el servidor',
      confirmButtonText: 'Aceptar'
    })
  })
}

function claseLinea(linea) {
  if (linea.includes('Error') || linea.includes('ERROR')) return 'linea-error'
  if (linea.includes('==='))                               return 'linea-separador'
  if (linea.includes('100%') || linea.includes('terminado')) return 'linea-exito'
  return 'linea-normal'
}

// ── Helpers ───────────────────────────────────────────────────────────────────
async function fetchReceivedChunks(fileId) {
  try {
    const res  = await fetch(`${API_BASE}/chunk/status/${fileId}`)
    const data = await res.json()
    return data.receivedChunks || []
  } catch {
    return []
  }
}

function getOrCreateFileId(file) {
  const key = 'upload_' + file.name + '_' + file.size
  let id = localStorage.getItem(key)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(key, id)
  }
  return id
}

function sleep(ms) {
  return new Promise(r => setTimeout(r, ms))
}

function cancelUpload() {
  cancelled.value   = true
  uploading.value   = false
  currentFile.value = ''
}

function reset() {
  files.value          = []
  done.value           = false
  uploadError.value    = ''
  globalProgress.value = 0
  speed.value          = ''
  cancelled.value      = false
  paused.value         = false
  currentFile.value    = ''
  uploadedCount.value  = 0
  // Resetear indexador también al cambiar la carpeta
  lineas.value         = []
  estadoFinal.value    = null
}
</script>

<style scoped>
/* ── Contenedor general ────────────────────────────────────────────────────── */
.panel {
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: 'Segoe UI', system-ui, sans-serif;
}

/* ── Pasos ─────────────────────────────────────────────────────────────────── */
.step {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
  transition: opacity 0.2s;
}
.step--done   { border-color: #10b981; background: #f0fdf4; }
.step--locked { opacity: 0.5; pointer-events: none; }

.step__header {
  display: flex;
  align-items: center;
  gap: 10px;
}
.step__num {
  width: 28px; height: 28px;
  border-radius: 50%;
  background: #3b82f6;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.step--done   .step__num { background: #10b981; }
.step--locked .step__num { background: #d1d5db; }

.step__title { font-size: 1rem; font-weight: 600; color: #111827; margin: 0; flex: 1; }

.step__badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 99px;
}
.step__badge--ok     { background: #dcfce7; color: #15803d; }
.step__badge--locked { background: #f3f4f6; color: #9ca3af; }
.step__badge--error  { background: #fee2e2; color: #b91c1c; }

/* ── Drop zone ─────────────────────────────────────────────────────────────── */
.drop-zone {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  transition: all 0.2s;
  background: #fafafa;
}
.drop-zone.active { border-color: #3b82f6; background: #eff6ff; }
.drop-zone.filled { border-style: solid; border-color: #10b981; background: #f0fdf4; }

.title    { font-size: 1rem; font-weight: 600; color: #111827; margin: 0 0 4px; }
.subtitle { font-size: 0.875rem; color: #6b7280; margin: 0; }

/* ── Progreso ──────────────────────────────────────────────────────────────── */
.progress-block { display: flex; flex-direction: column; gap: 6px; }
.progress-bar   { height: 8px; background: #e5e7eb; border-radius: 99px; overflow: hidden; }
.progress-fill  { height: 100%; background: #3b82f6; border-radius: 99px; transition: width 0.3s; }
.progress-stats { display: flex; justify-content: space-between; font-size: 0.8rem; color: #6b7280; }
.current-file   { font-size: 0.8rem; color: #6b7280; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ── Alertas ───────────────────────────────────────────────────────────────── */
.alert         { padding: 12px 14px; border-radius: 8px; font-size: 0.875rem; }
.alert--warn   { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }
.alert--ok     { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
.alert--error  { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }

/* ── Botones ───────────────────────────────────────────────────────────────── */
.actions { display: flex; gap: 10px; }
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
}
.btn--primary { background: #3b82f6; color: #fff; flex: 1; }
.btn--primary:hover:not(:disabled) { background: #2563eb; }
.btn--primary:disabled { background: #93c5fd; cursor: not-allowed; }
.btn--outline { background: transparent; border: 1px solid #d1d5db; color: #374151; flex: 1; }
.btn--outline:hover { background: #f9fafb; }
.btn--ghost   { background: transparent; color: #6b7280; border: 1px solid #e5e7eb; }
.btn--ghost:hover { background: #f9fafb; }

/* ── Trámite ───────────────────────────────────────────────────────────────── */
.tramite-info { font-size: 0.9rem; color: #374151; margin: 0; }

/* ── Consola ───────────────────────────────────────────────────────────────── */
.consola {
  background: #1e1e1e;
  color: #d4d4d4;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  padding: 16px;
  border-radius: 6px;
  max-height: 320px;
  overflow-y: auto;
}
.linea-normal    { color: #d4d4d4; margin: 1px 0; }
.linea-separador { color: #569cd6; font-weight: bold; margin: 1px 0; }
.linea-exito     { color: #4ec9b0; margin: 1px 0; }
.linea-error     { color: #f44747; margin: 1px 0; }
</style>